/**
 * Visitor heartbeat — keeps `visitors.visit_count` + `total_engaged_secs`
 * moving even when the visitor never opens chat. The R1 visitor-state
 * banner ("welcome back, we noticed you read X last visit") needs this:
 * without it, a returning visitor who bounces without chatting still
 * looks like first_time to the next session.
 *
 * Cadence:
 *   - Boot ping: deltaEngagedSecs=0, fired once on widget init.
 *   - Heartbeat: every 30s while document.visibilityState === 'visible'.
 *     Sends accumulated engaged seconds since the last ping, capped
 *     at 120s server-side so a backgrounded tab can't credit hours.
 *   - On hide / pagehide: sendBeacon flush of any pending delta so we
 *     don't lose engagement when the visitor closes the tab mid-window.
 *
 * Failure is silent — the visitor-row state is non-critical for the
 * current page; the next chat turn's getOrCreateVisitor will resync.
 */

const HEARTBEAT_MS = 30_000;
const MAX_DELTA_SECS = 120;

export type VisitorHeartbeatStart = {
  apiBaseUrl: string;
  publicId: string;
  fingerprint: string;
  /** Override for tests — defaults to the live page URL. */
  getPageUrl?: () => string;
};

export function startVisitorHeartbeat(opts: VisitorHeartbeatStart): () => void {
  const url = `${opts.apiBaseUrl.replace(/\/+$/, '')}/v1/visitor/ping`;
  const pageUrl = () =>
    (opts.getPageUrl ? opts.getPageUrl() : window.location.href).slice(0, 2000);

  // The "engagement clock" — running while the page is visible.
  // We accumulate seconds locally + flush on each heartbeat / hide.
  let visibleSince: number | null =
    typeof document !== 'undefined' && document.visibilityState === 'visible'
      ? Date.now()
      : null;
  let pendingDeltaMs = 0;

  // Boot ping: zero delta, just so the row exists + visit_count bumps
  // before any chat or engagement happens.
  void send({ deltaSecs: 0, beacon: false });

  function flushDelta(beacon: boolean): void {
    let delta = pendingDeltaMs;
    pendingDeltaMs = 0;
    if (visibleSince != null) {
      delta += Date.now() - visibleSince;
      // Restart the engagement clock ONLY if the page is still
      // foregrounded. A hide / pagehide flush stops it (null) so
      // background time is never credited — otherwise the 30s interval
      // keeps crediting a backgrounded tab ~1:1 with wall-clock and a
      // tab left open overnight books hours of fake "engaged" time.
      const stillVisible =
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible';
      visibleSince = !beacon && stillVisible ? Date.now() : null;
    }
    const secs = Math.min(MAX_DELTA_SECS, Math.floor(delta / 1000));
    if (secs <= 0) return;
    void send({ deltaSecs: secs, beacon });
  }

  function send({
    deltaSecs,
    beacon,
  }: {
    deltaSecs: number;
    beacon: boolean;
  }): Promise<void> {
    // HubSpot Phase 1A — piggyback on the customer's HubSpot tracking
    // cookie (same-origin read on customer.com). When present, server
    // uses it to resolve the visitor to a known HubSpot contact for
    // first-party CRM enrichment. Missing on non-HubSpot sites; that's
    // fine — server gates on its own integration record before calling.
    const hubspotutk = readCookie('hubspotutk');
    // Pardot Phase 2 — the `visitor_id<accountId>` first-party cookie (the
    // hubspotutk analog). Present ONLY when the customer runs first-party
    // Pardot tracking (a CNAME on their own domain), so it's readable
    // same-origin; default pi.pardot.com tracking is cross-origin and we
    // simply won't find it. Server resolves it to a known prospect.
    const pardotVisitorId = parsePardotVisitorId(
      typeof document !== 'undefined' ? document.cookie : '',
    );
    const body = JSON.stringify({
      publicId: opts.publicId,
      fingerprint: opts.fingerprint,
      pageUrl: pageUrl(),
      deltaEngagedSecs: deltaSecs,
      ...(hubspotutk ? { hubspotutk } : {}),
      ...(pardotVisitorId ? { pardotVisitorId } : {}),
    });
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // sendBeacon is the only reliable transport on pagehide — fetch
      // gets cancelled. Blob keeps content-type correct on the server.
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return Promise.resolve();
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      // keepalive lets a fetch-during-unload still complete (browser
      // background-fetches it after the tab is gone).
      keepalive: true,
      credentials: 'include',
    }).then(
      () => undefined,
      () => undefined,
    );
  }

  const interval = setInterval(() => flushDelta(false), HEARTBEAT_MS);

  function onVisibility(): void {
    if (document.visibilityState === 'visible') {
      visibleSince = Date.now();
    } else {
      // Tab hidden — credit whatever was accumulated and stop the clock
      // until we come back. Use beacon=false for the hide event (fetch
      // is still alive); the pagehide handler uses beacon=true.
      flushDelta(false);
    }
  }

  function onPageHide(): void {
    flushDelta(true);
  }

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  return function stop(): void {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    flushDelta(false);
  };
}

/**
 * Read a single cookie value from `document.cookie`. Returns null
 * when missing OR when cookies aren't accessible (rare — SSR, perms).
 * Capped at 128 chars to match the server-side schema limit.
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const all = document.cookie ?? '';
  const prefix = `${name}=`;
  for (const part of all.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      try {
        const raw = decodeURIComponent(trimmed.slice(prefix.length));
        return raw.slice(0, 128);
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Extract the Pardot visitor id from a cookie string. Pardot's first-party
 * tracking sets a cookie named `visitor_id<accountId>` (e.g.
 * `visitor_id551052=940939994`) whose value is the numeric Visitor id — the
 * server feeds it to the Pardot Visitor API to resolve a known prospect. The
 * account-id suffix varies per customer, so we match by PREFIX, not a fixed
 * name, and skip the `visitor_id<accountId>-hash` integrity sibling. Returns
 * the numeric value (or null). Exported for unit testing.
 */
export function parsePardotVisitorId(cookieString: string): string | null {
  if (!cookieString) return null;
  for (const part of cookieString.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    const name = trimmed.slice(0, eq);
    // `visitor_id` + digits, but NOT the `…-hash` companion.
    if (!/^visitor_id\d+$/.test(name)) continue;
    const value = trimmed.slice(eq + 1).trim();
    if (/^\d{1,20}$/.test(value)) return value;
  }
  return null;
}
